import * as kinds from '@nostr/tools/kinds'
import { getPollResponseFromEvent } from '@/lib/event-metadata'
import { now } from '@/lib/utils'
import { Filter } from '@nostr/tools/filter'
import LRUCache from '@fiatjaf/lru-cache/lru-cache'
import { fetchEvents } from './client.service'

export type TPollResults = {
  totalVotes: number
  results: Record<string, Set<string>>
  voters: Set<string>
  updatedAt: number
}

const pollResultsCache = new LRUCache<string, TPollResults>(100)
const pollResultsSubscribers = new Map<string, Set<() => void>>()

export async function fetchPollResults(
  pollEventId: string,
  relays: string[],
  validPollOptionIds: string[],
  isMultipleChoice: boolean,
  endsAt?: number
) {
  const filter: Filter = {
    kinds: [kinds.PollResponse],
    '#e': [pollEventId],
    limit: 1000
  }

  if (endsAt) {
    filter.until = endsAt
  }

  let results = pollResultsCache.get(pollEventId)
  if (results) {
    if (endsAt && results.updatedAt >= endsAt) {
      return results
    }
    filter.since = results.updatedAt
  } else {
    results = {
      totalVotes: 0,
      results: validPollOptionIds.reduce(
        (acc, optionId) => {
          acc[optionId] = new Set<string>()
          return acc
        },
        {} as Record<string, Set<string>>
      ),
      voters: new Set<string>(),
      updatedAt: 0
    }
  }

  const responseEvents = await fetchEvents(relays, filter)

  results.updatedAt = now()

  const responses = responseEvents
    .map((evt) => getPollResponseFromEvent(evt, validPollOptionIds, isMultipleChoice))
    .filter((response): response is NonNullable<typeof response> => response !== null)

  for (const response of responses.sort((a, b) => b.created_at - a.created_at)) {
    if (results && results.voters.has(response.pubkey)) continue
    results.voters.add(response.pubkey)

    results.totalVotes += response.selectedOptionIds.length
    for (const optionId of response.selectedOptionIds) {
      if (results.results[optionId]) {
        results.results[optionId].add(response.pubkey)
      }
    }
  }

  pollResultsCache.set(pollEventId, results)
  if (responseEvents.length) {
    notifyPollResults(pollEventId)
  }
  return results
}

export function subscribePollResults(pollEventId: string, callback: () => void) {
  let set = pollResultsSubscribers.get(pollEventId)
  if (!set) {
    set = new Set()
    pollResultsSubscribers.set(pollEventId, set)
  }
  set.add(callback)
  return () => {
    set?.delete(callback)
    if (set?.size === 0) pollResultsSubscribers.delete(pollEventId)
  }
}

function notifyPollResults(pollEventId: string) {
  const set = pollResultsSubscribers.get(pollEventId)
  if (set) {
    for (const cb of set) cb()
  }
}

export function getPollResults(id: string): TPollResults | undefined {
  return pollResultsCache.get(id)
}

export function addPollResponse(pollEventId: string, pubkey: string, selectedOptionIds: string[]) {
  const results = pollResultsCache.get(pollEventId)
  if (!results) return

  if (results.voters.has(pubkey)) return

  results.voters.add(pubkey)
  results.totalVotes += selectedOptionIds.length
  for (const optionId of selectedOptionIds) {
    if (results.results[optionId]) {
      results.results[optionId].add(pubkey)
    }
  }

  pollResultsCache.set(pollEventId, { ...results })
  notifyPollResults(pollEventId)
}
